import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  codeSnippetCopied,
  pngToFaviconAsync,
  selectState,
} from "./FaviconGeneratorSlice";
import { useTranslation } from "react-i18next";
import { Button, IconButton, Stack, Typography, styled } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { Box } from "@mui/system";
import { Light as SyntaxHighlighter } from "react-syntax-highlighter";
import js from "react-syntax-highlighter/dist/esm/languages/hljs/xml";
import docco from "react-syntax-highlighter/dist/esm/styles/hljs/androidstudio";
import CopyIcon from "@mui/icons-material/ContentCopy";
import TaskAlt from "@mui/icons-material/TaskAlt";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import { SideMenuPageContainer } from "../../components/side_menu_page_container/SideMenuPageContainer";
import { HomeCard } from "../../components/home_card/HomeCard";
import AndroidIosImageResizerImage from "../../assets/AndroidIosImageResizer.webp";
import ImageConverterImage from "../../assets/ImageConverter.webp";
import AdSense from "../../components/adsense/AdSense";
import { ADSENSE_SLOTS } from "../../config/adsense";

SyntaxHighlighter.registerLanguage("xml", js);

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export function FaviconGenerator() {
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectState);
  const { t } = useTranslation();

  return (
    <SideMenuPageContainer
      pageTitle={t("faviconGenerator.title")}
      pageSeoTitle={t("faviconGenerator.seoTitle")}
      pageDescription={t("faviconGenerator.description")}
    >
      <Box sx={{ width: { xs: 300, sm: 350, md: 600, lg: 700 } }}>
        <Grid2
          container
          spacing={2}
          sx={{ mt: 2 }}
          columns={{ xs: 6, sm: 6, md: 12, lg: 12 }}
        >
          <Grid2 xs={12}>
            <Typography variant="h2" sx={{ mb: 2 }}>
              {t("faviconGenerator.mainHeading")}
            </Typography>
          </Grid2>
          <Grid2 xs={12}>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <b style={{ color: "#FFD9A8" }}>{t("faviconGenerator.step1")}</b> {t("faviconGenerator.step1Description")} <br />
              {t("faviconGenerator.step1Note")}
            </Typography>
          </Grid2>
          <Grid2 xs={12}>
            <Stack
              direction="column"
              justifyContent="center"
              alignItems="center"
            >
              <Button
                component="label"
                variant="contained"
                startIcon={<CloudUploadIcon sx={{ color: "#FFD9A8" }} />}
                onChange={(e: any) =>
                  dispatch(pngToFaviconAsync(e.target.files[0]))
                }
              >
                <Typography>{t("faviconGenerator.uploadPng")}</Typography>
                <VisuallyHiddenInput type="file" accept="image/png" />
              </Button>
            </Stack>
          </Grid2>
          <Grid2 xs={12}>
            <Box sx={{ mt: 2 }} />
            <Typography variant="body1">
              <b style={{ color: "#FFD9A8" }}>{t("faviconGenerator.step2")}</b> {t("faviconGenerator.step2Description")}
            </Typography>
          </Grid2>
          <Grid2 xs={12}>
            <Stack
              direction="column"
              justifyContent="center"
              alignItems="center"
            >
              <Box sx={{ width: { xs: 300, sm: 350, md: 600, lg: 700 } }}>
                <Box
                  sx={{
                    mb: -1.8,
                    bgcolor: "#282B2E",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="helptext" sx={{ ml: 1, pt: 1, pb: 1 }}>
                    {t("faviconGenerator.html")}
                  </Typography>
                  <IconButton
                    aria-label={t("faviconGenerator.copy")}
                    sx={{ color: "#A5D9EE" }}
                    onClick={() => {
                      navigator.clipboard.writeText(faviconHtmlHeaderString);
                      dispatch(codeSnippetCopied(true));
                    }}
                  >
                    {state.codesnippetCopied ? (
                      <TaskAlt sx={{ color: "#97e286" }} />
                    ) : (
                      <CopyIcon />
                    )}
                  </IconButton>
                </Box>
                <SyntaxHighlighter language="xml" style={docco}>
                  {faviconHtmlHeaderString}
                </SyntaxHighlighter>
              </Box>
            </Stack>
          </Grid2>
          <Grid2 xs={12}>
            <AdSense
                adSlot={ADSENSE_SLOTS.IN_ARTICLE}
                adFormat="fluid"
                adLayoutKey="-fb+5w+4e-db+86"
                sx={{ mb: 3 }}
              />
            <Typography variant="h2" sx={{ mb: 3, mt: 5 }}>
              {t("faviconGenerator.content.recommendedFormatsHeading")}
            </Typography>
            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("faviconGenerator.content.icoHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("faviconGenerator.content.icoDescription")}
            </Typography>
            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("faviconGenerator.content.googleHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("faviconGenerator.content.googleDescription")}
            </Typography>
            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("faviconGenerator.content.appleHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("faviconGenerator.content.appleDescription")}
            </Typography>
            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("faviconGenerator.content.manifestHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("faviconGenerator.content.manifestDescription")}
            </Typography>
            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("faviconGenerator.content.svgHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t("faviconGenerator.content.svgDescription")}
            </Typography>
          </Grid2>
          <Grid2 xs={12}>
            <Typography variant="h3" sx={{ mt: 3, mb: 1 }}>
              {t("faviconGenerator.related")}
            </Typography>
          </Grid2>

          <Grid2 xs={6}>
            <HomeCard
              languageResource="androidIosResizer"
              link="/AndroidIosImageResizer"
              smallCard={true}
              image={AndroidIosImageResizerImage}
            />
          </Grid2>
          <Grid2 xs={6}>
            <HomeCard
              languageResource="imageConverter"
              link="/ImageConverter"
              smallCard={true}
              image={ImageConverterImage}
            />
          </Grid2>
        </Grid2>
      </Box>
    </SideMenuPageContainer>
  );
}

const faviconHtmlHeaderString = `<!-- Backwards compatability -->
<link rel="icon" sizes="16x16 24x24 32x32 64x64" href="favicon.ico">

<!-- All other browsers -->
<link rel="icon" type="image/png" sizes="32x32" href="favicon-32x32.png">
<link rel="icon" type="image/png" sizes="16x16" href="favicon-16x16.png">

<!-- Google Search and Android Chrome -->
<link rel="icon" type="image/png" sizes="48x48" href="favicon-48x48.png">
<link rel="icon" type="image/png" sizes="192x192" href="favicon-192x192.png">

<!-- iPhone -->
<link rel="apple-touch-icon" type="image/png" sizes="180x180" href="favicon-180x180.png">

<!-- iPad Retina -->
<link rel="apple-touch-icon" type="image/png" sizes="167x167" href="favicon-167x167.png">

<!-- Other iPads -->
<link rel="apple-touch-icon" type="image/png" sizes="152x152" href="favicon-152x152.png">

<!-- Web application manifest -->
<link rel="manifest" href="manifest.json">`;
